import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { analyticsApi } from '../lib/api';
import { ArrowLeft, Calendar, MessageSquare, Star, User, Filter } from 'lucide-react';

interface Session {
  id: string;
  name: string;
  email: string;
  category_id: number;
  session_type: string;
  purpose: string;
  created_at: string;
  messages: Array<{
    id: string;
    role: string;
    content: string;
    created_at: string;
  }>;
  feedbacks?: {
    rating: number;
    comment: string;
    created_at: string;
  };
  category?: {
    id: number;
    name: string;
  };
}

const SESSION_TYPE_STYLES: Record<string, string> = {
  user: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300',
  vmp: 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300',
};

const PURPOSE_STYLES: Record<string, string> = {
  general: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300',
  'objective-generation': 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300',
  assessment: 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-300',
};

const UserDetailPage: React.FC = () => {
  const { email } = useParams<{ email: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    session_type: searchParams.get('session_type') || '',
    purpose: searchParams.get('purpose') || '',
  });

  useEffect(() => {
    const fetchUserSessions = async () => {
      if (!email) return;
      
      try {
        setLoading(true);
        const activeFilters: { session_type?: string; purpose?: string } = {};
        if (filters.session_type) activeFilters.session_type = filters.session_type;
        if (filters.purpose) activeFilters.purpose = filters.purpose;
        const response = await analyticsApi.getUserSessions(
          decodeURIComponent(email),
          1,
          100,
          Object.keys(activeFilters).length > 0 ? activeFilters : undefined
        );
        setSessions(response.sessions);
        setError(null);
      } catch (error: any) {
        setError(error.response?.data?.error || 'Failed to fetch user sessions');
      } finally {
        setLoading(false);
      }
    };

    fetchUserSessions();
  }, [email, filters]);

  const handleFilterChange = (key: string, value: string) => {
    setFilters(f => ({ ...f, [key]: value }));
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    setSearchParams(params, { replace: true });
  };

  const clearFilters = () => {
    setFilters({ session_type: '', purpose: '' });
    setSearchParams({}, { replace: true });
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  const userInfo = sessions[0];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/dashboard/users')}
          className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            User Sessions
          </h1>
          {userInfo && (
            <div className="flex items-center space-x-4 text-gray-600 dark:text-gray-400">
              <div className="flex items-center space-x-2">
                <User className="h-4 w-4" />
                <span>{userInfo.name}</span>
              </div>
              <span>•</span>
              <span>{email}</span>
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm p-4 border border-gray-200 dark:border-dark-700">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <Filter className="h-4 w-4" />
            Filters
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-gray-400">Session Type:</label>
            <select
              value={filters.session_type}
              onChange={(e) => handleFilterChange('session_type', e.target.value)}
              className="text-sm border border-gray-300 dark:border-dark-600 rounded-lg px-3 py-1.5 bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All</option>
              <option value="user">User</option>
              <option value="vmp">VMP</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-gray-400">Purpose:</label>
            <select
              value={filters.purpose}
              onChange={(e) => handleFilterChange('purpose', e.target.value)}
              className="text-sm border border-gray-300 dark:border-dark-600 rounded-lg px-3 py-1.5 bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All</option>
              <option value="general">General</option>
              <option value="objective-generation">Objective Generation</option>
              <option value="assessment">Assessment</option>
            </select>
          </div>
          {(filters.session_type || filters.purpose) && (
            <button
              onClick={clearFilters}
              className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Sessions */}
      <div className="space-y-4">
        {sessions.map((session) => (
          <div key={session.id} className="bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-200 dark:border-dark-700 overflow-hidden">
            {/* Session Header */}
            <div className="p-6 border-b border-gray-200 dark:border-dark-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Session #{session.id.slice(-8)}
                    </h3>
                    <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400 mt-1">
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-4 w-4" />
                        <span>
                          {new Date(session.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      {session.category && (
                        <span className="bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-300 px-2 py-1 rounded text-xs">
                          {session.category.name}
                        </span>
                      )}
                      <span className={`px-2 py-1 rounded text-xs font-medium ${SESSION_TYPE_STYLES[session.session_type] || SESSION_TYPE_STYLES.user}`}>
                        {(session.session_type || 'user').toUpperCase()}
                      </span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${PURPOSE_STYLES[session.purpose] || PURPOSE_STYLES.general}`}>
                        {(session.purpose || 'general').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                    <MessageSquare className="h-4 w-4" />
                    <span>{session.messages.length} messages</span>
                  </div>
                  {session.feedbacks && (
                    <div className="flex items-center space-x-2 text-sm text-yellow-600 dark:text-yellow-400">
                      <Star className="h-4 w-4" />
                      <span>{session.feedbacks.rating}/5</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="p-6">
              <div className="space-y-4">
                {session.messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                        message.role === 'user'
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 dark:bg-dark-700 text-gray-900 dark:text-white'
                      }`}
                    >
                      <p className="text-sm">{message.content}</p>
                      <p className={`text-xs mt-1 ${
                        message.role === 'user'
                          ? 'text-primary-200'
                          : 'text-gray-500 dark:text-gray-400'
                      }`}>
                        {new Date(message.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Feedback */}
              {session.feedbacks && (
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-dark-700">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                    User Feedback
                  </h4>
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <Star className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                      <span className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                        {session.feedbacks.rating}/5 Stars
                      </span>
                    </div>
                    {session.feedbacks.comment && (
                      <p className="text-sm text-yellow-700 dark:text-yellow-200">
                        "{session.feedbacks.comment}"
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}

        {sessions.length === 0 && (
          <div className="text-center py-12">
            <MessageSquare className="h-12 w-12 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">
              No sessions found for this user.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDetailPage;